import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Pencil, Save, X, CheckCircle2, Eye, EyeOff, Upload, Image as ImageIcon, Sparkles, Loader2 } from 'lucide-react';
import RichHtmlEditor from '../../lib/RichHtmlEditor';
import { postsApi, uploadsApi } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const empty = {
  title: '', slug: '', excerpt: '', content: '', tag: 'News',
  cover_image: '', published: true, seo_title: '', seo_description: '',
};

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState('');
  const coverInputRef = useRef(null);
  const inlineInputRef = useRef(null);

  const refresh = () => { postsApi.listAdmin().then(setItems).catch(() => setItems([])); };
  useEffect(refresh, []);

  const startNew = () => setEditing({ ...empty });
  const edit = (p) => setEditing({ ...p });

  const translateAll = async () => {
    if (!window.confirm('Auto-translate every post (title, excerpt, content, SEO) into all 15 other languages? Existing translations are preserved. This may take a couple of minutes for a large blog.')) return;
    setTranslating(true); setTranslateMsg('Translating…');
    try {
      const res = await postsApi.translateAll();
      setTranslateMsg(`Done — translated ${res.translated_fields} fields across ${res.posts} posts into ${(res.langs || []).length} languages.`);
      setTimeout(() => setTranslateMsg(''), 7000);
      refresh();
    } catch (err) {
      setTranslateMsg(err?.response?.data?.detail || 'Auto-translate failed');
    } finally {
      setTranslating(false);
    }
  };

  const save = async () => {
    if (!editing.title) return alert('Title is required');
    const payload = {
      ...editing,
      slug: editing.slug || editing.title,
    };
    if (editing.id) {
      await postsApi.update(editing.id, payload);
    } else {
      await postsApi.create(payload);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setEditing(null);
    refresh();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await postsApi.remove(id);
    refresh();
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadsApi.uploadImage(file);
      setEditing((ed) => ({ ...ed, cover_image: res.url }));
    } catch (err) {
      alert('Upload failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const uploadInline = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadsApi.uploadImage(file);
      const url = uploadsApi.publicUrl(res.url);
      const md = `\n\n![${file.name}](${url})\n\n`;
      setEditing((ed) => ({ ...ed, content: (ed.content || '') + md }));
    } catch (err) {
      alert('Upload failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (inlineInputRef.current) inlineInputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0b1f3a]">News / Blog</h2>
          <p className="text-slate-500 text-sm mt-1">Add and edit articles displayed on the public blog.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {saved && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 size={16} /> Saved</span>}
          {!editing && (
            <>
              <button
                onClick={translateAll}
                disabled={translating}
                data-testid="admin-blog-translate-all"
                className="btn-primary disabled:opacity-60"
                title="Auto-translate every post into all 15 other languages"
              >
                {translating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {translating ? 'Translating…' : 'Translate all to 16 languages'}
              </button>
              <button onClick={startNew} className="btn-primary" data-testid="admin-blog-new"><Plus size={16} /> New post</button>
            </>
          )}
        </div>
      </div>
      {translateMsg && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">
          {translateMsg}
        </div>
      )}

      {editing ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold text-[#0b1f3a]">{editing.id ? 'Edit post' : 'New post'}</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="field-label">Title</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <div>
              <Label className="field-label">Slug</Label>
              <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto from title" />
            </div>
            <div>
              <Label className="field-label">Tag</Label>
              <Input value={editing.tag} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} placeholder="News / Home Insurance / ..." />
            </div>
            <div className="md:col-span-2">
              <Label className="field-label">Cover image</Label>
              <div className="flex items-start gap-4">
                {editing.cover_image ? (
                  <div className="relative w-40 h-28 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shrink-0">
                    <img
                      src={uploadsApi.publicUrl(editing.cover_image)}
                      alt="cover"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, cover_image: '' })}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-slate-700 hover:bg-white inline-flex items-center justify-center shadow"
                      title="Remove"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="w-40 h-28 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
                    <ImageIcon size={22} />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input
                    value={editing.cover_image || ''}
                    onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                    placeholder="Paste an image URL or upload"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploading}
                      className="btn-outline !py-2 !px-3 text-xs disabled:opacity-60"
                    >
                      <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload cover'}
                    </button>
                    <p className="text-xs text-slate-500">JPG / PNG / WebP up to 8 MB</p>
                  </div>
                  <input ref={coverInputRef} type="file" accept="image/*" hidden onChange={uploadCover} />
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="field-label">Excerpt</Label>
              <Textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="field-label !mb-0">Content</Label>
                <p className="text-xs text-slate-500">Paste from Word, Google Docs or LibreOffice - formatting is preserved</p>
              </div>
              <RichHtmlEditor
                value={editing.content || ''}
                onChange={(html) => setEditing({ ...editing, content: html })}
                placeholder={'Write your article here...\n\nUse the H1 / H2 / H3 buttons for headings, or paste content straight from Word / Google Docs / LibreOffice - it will be cleaned up automatically.'}
                minHeight={520}
                testid="admin-blog-content"
              />
              <p className="text-xs text-slate-500 mt-1">
                Full WYSIWYG HTML editor - headings, bold, links, images, tables. Paste from Word/Docs/LibreOffice works out of the box.
              </p>
            </div>
            <div>
              <Label className="field-label">SEO title</Label>
              <Input value={editing.seo_title || ''} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} />
            </div>
            <div>
              <Label className="field-label">SEO description</Label>
              <Input value={editing.seo_description || ''} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch checked={!!editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
              <span className="text-sm text-slate-700">Published (visible on public blog)</span>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-2">
            <button onClick={save} className="btn-primary"><Save size={16} /> Save post</button>
            <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {items.length === 0 ? (
            <p className="p-10 text-center text-slate-500 text-sm">No posts yet. Click <b>New post</b> to add one.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Title</th>
                  <th className="text-left px-4 py-3 font-semibold">Tag</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-[#0b1f3a]">{p.title}</p>
                      <p className="text-xs text-slate-500">/{p.slug}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{p.tag}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        p.published ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {p.published ? <><Eye size={11} /> Live</> : <><EyeOff size={11} /> Draft</>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{new Date(p.updated_at || p.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => edit(p)} className="text-slate-500 hover:text-[#0d3a8a] mr-3" title="Edit"><Pencil size={16} /></button>
                      <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-rose-600" title="Delete"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
